import { triniumApi } from '@api/Trinium/slice';
import {
  AcceptEdiLoadTenderMutation,
  DeleteEdiLoadTenderMutation,
  FetchEdiLoadTenderDetailsRequest,
  FetchEdiLoadTenderRequest,
  LoadTender,
  PatchEdiLoadTenderMutation,
  RejectEdiLoadTenderMutation,
  transformLoadTenderResponses,
  transformLoadTenderResponse,
  updateEdiLoadTenderCache
} from './types';

export const ediSlice = triniumApi.injectEndpoints({
  endpoints: (builder) => ({
    fetchLoadTenders: builder.query<LoadTender[], FetchEdiLoadTenderRequest>({
      query: ({ terminalNumber, status, modifyDateFrom, modifyDateTo }) => ({
        url: `/${terminalNumber}/edi/204`,
        params: {
          status,
          modifyDateFrom,
          modifyDateTo
        }
      }),
      transformResponse: transformLoadTenderResponses,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'edi' as const,
                id: id
              })),
              'edi'
            ]
          : []
    }),

    fetchLoadTender: builder.query<LoadTender, FetchEdiLoadTenderDetailsRequest>({
      query: ({ terminalNumber, id }) => ({
        url: `/${terminalNumber}/edi/204/${String(id)}`
      }),
      transformResponse: transformLoadTenderResponse,
      providesTags: (_result, _error, { id }) => [{ type: 'edi', id }]
    }),

    patchLoadTender: builder.mutation<LoadTender, PatchEdiLoadTenderMutation>({
      query: ({ terminalNumber, formValues: { id, ...mutations } }) => ({
        url: `/${terminalNumber}/edi/204/${String(id)}`,
        method: 'PATCH',
        body: {
          ...mutations
        }
      }),
      transformResponse: transformLoadTenderResponse,
      async onQueryStarted(_params, { queryFulfilled }) {
        const { data: loadTender } = await queryFulfilled;
        updateEdiLoadTenderCache([loadTender], { replaceDetail: true });
      }
    }),

    acceptLoadTender: builder.mutation<LoadTender[], AcceptEdiLoadTenderMutation>({
      query: ({ terminalNumber, ids }) => ({
        url: `/${terminalNumber}/edi/204`,
        method: 'PUT',
        body: {
          action: 'accept',
          data: {
            ids: [...ids]
          }
        }
      }),
      transformResponse: transformLoadTenderResponses,
      async onQueryStarted(_params, { queryFulfilled }) {
        const { data: response } = await queryFulfilled;
        updateEdiLoadTenderCache(response, { replaceDetail: true });
      },
      invalidatesTags: ['dispatch']
    }),

    rejectLoadTender: builder.mutation<LoadTender, RejectEdiLoadTenderMutation>({
      query: ({ terminalNumber, id, rejectionReasons }) => ({
        url: `/${terminalNumber}/edi/204/${String(id)}`,
        method: 'PUT',
        body: {
          action: 'reject',
          data: {
            rejectReasonIds: [...rejectionReasons]
          }
        }
      }),
      transformResponse: transformLoadTenderResponse,
      async onQueryStarted(_params, { queryFulfilled }) {
        const { data: response } = await queryFulfilled;
        updateEdiLoadTenderCache([response], { replaceDetail: true });
      },
      invalidatesTags: ['dispatch']
    }),

    deleteLoadTender: builder.mutation<null, DeleteEdiLoadTenderMutation>({
      query: ({ terminalNumber, id }) => ({
        url: `/${terminalNumber}/edi/204/${String(id)}`,
        method: 'DELETE'
      }),
      async onQueryStarted(params, { queryFulfilled }) {
        await queryFulfilled;
        updateEdiLoadTenderCache([{ id: params.id, status: 'Deleted' }]);
      }
    })
  })
});

export const {
  useFetchLoadTendersQuery,
  useFetchLoadTenderQuery,
  usePatchLoadTenderMutation,
  useAcceptLoadTenderMutation,
  useRejectLoadTenderMutation,
  useDeleteLoadTenderMutation
} = ediSlice;

export const LoadTenderMutationFixedKeyTemplate = 'edi-load-tender-mutation-';
